/**
 * Tree Renderer — 2D hierarchical layout using SVG
 * Renders a top-down tree (or forest) of concepts connected by hierarchical edges.
 */

import { getNodeColorFromTags } from '../lib/colorUtils.js';

const NS = 'http://www.w3.org/2000/svg';

interface TreeColors {
  edge: string;
  edgeHover: string;
  nodeFill: string;
  nodeHover: string;
  text: string;
  textDim: string;
}

const COLORS: TreeColors = {
  edge: '#5c584e',
  edgeHover: '#e8e4db',
  nodeFill: '#1a1917',
  nodeHover: '#111110',
  text: '#e8e4db',
  textDim: '#5c584e',
};

interface TreeDefaults {
  nodeW: number;
  nodeH: number;
  hGap: number;
  vGap: number;
  forestGap: number;
  padding: number;
}

const DEFAULTS: TreeDefaults = {
  nodeW: 180,
  nodeH: 42,
  hGap: 40,   // horizontal gap between siblings
  vGap: 80,   // vertical gap between levels
  forestGap: 60, // gap between separate trees
  padding: 60,
};

interface TreeNode {
  slug: string;
  title?: string;
  tags?: { color: string; name?: string; id?: number }[];
}

interface TreeEdgeData {
  parent: string;
  child: string;
  label?: string;
  [key: string]: unknown;
}

interface TreeData {
  nodes: TreeNode[];
  edges: TreeEdgeData[];
}

interface TreeRendererOptions {
  onNodeClick?: (slug: string) => void;
  onEdgeClick?: (info: { edgeData: TreeEdgeData | null; parentSlug: string; childSlug: string }) => void;
}

interface LayoutNode {
  slug: string;
  depth: number;
  x: number;
  y: number;
  children: LayoutNode[];
  subtreeW: number;
}

interface FlatEdge {
  from: LayoutNode;
  to: LayoutNode;
  data: TreeEdgeData | null;
}

export interface TreeRendererInstance {
  clearEdgeHighlight(): void;
  highlightNodes(slugs: string[] | null): void;
  destroy(): void;
}

export function createTreeRenderer(
  container: HTMLElement,
  data: TreeData,
  options: TreeRendererOptions = {}
): TreeRendererInstance {
  const { onNodeClick = () => {}, onEdgeClick = () => {} } = options;
  const { nodeW, nodeH, hGap, vGap, forestGap, padding } = DEFAULTS;

  if (!data.nodes.length) return { clearEdgeHighlight() {}, highlightNodes() {}, destroy() {} };

  // --- Build tree structure ---
  const nodeMap = new Map<string, TreeNode>();
  data.nodes.forEach(n => nodeMap.set(n.slug, n));

  const childrenOf = new Map<string, string[]>(); // parent slug -> [child slugs]
  const hasParent = new Set<string>();

  for (const edge of data.edges) {
    if (!edge.parent || !edge.child) continue;
    if (!childrenOf.has(edge.parent)) childrenOf.set(edge.parent, []);
    childrenOf.get(edge.parent)!.push(edge.child);
    hasParent.add(edge.child);
  }

  // Find roots (nodes that are never a child)
  let roots = data.nodes.filter(n => !hasParent.has(n.slug)).map(n => n.slug);
  // If no roots found (cycle), pick the first node
  if (roots.length === 0 && data.nodes.length > 0) {
    roots = [data.nodes[0].slug];
  }

  // --- Layout each tree using a simple recursive algorithm ---
  // Each node gets { slug, x, y, w, children[] }
  // Nodes with multiple parents appear under each parent.
  // `ancestors` tracks the current path to prevent cycles only.

  function buildLayout(slug: string, depth: number, ancestors: Set<string>): LayoutNode | null {
    if (ancestors.has(slug)) return null; // cycle guard (not multi-parent guard)
    ancestors.add(slug);

    const kids = (childrenOf.get(slug) || [])
      .map(c => buildLayout(c, depth + 1, new Set(ancestors)))
      .filter((n): n is LayoutNode => n !== null);

    ancestors.delete(slug);

    const node: LayoutNode = {
      slug,
      depth,
      x: 0,
      y: depth * (nodeH + vGap),
      children: kids,
      subtreeW: 0,
    };

    if (kids.length === 0) {
      node.subtreeW = nodeW;
    } else {
      node.subtreeW = kids.reduce((sum, k) => sum + k.subtreeW, 0) + hGap * (kids.length - 1);
    }

    return node;
  }

  const trees = roots.map(r => buildLayout(r, 0, new Set<string>())).filter((n): n is LayoutNode => n !== null);

  // Assign x positions: center parent over children
  function assignX(node: LayoutNode, left: number): void {
    if (node.children.length === 0) {
      node.x = left + node.subtreeW / 2;
      return;
    }
    let childLeft = left;
    for (const child of node.children) {
      assignX(child, childLeft);
      childLeft += child.subtreeW + hGap;
    }
    // Center over children
    const firstChild = node.children[0];
    const lastChild = node.children[node.children.length - 1];
    node.x = (firstChild.x + lastChild.x) / 2;
  }

  let xOffset = 0;
  for (const tree of trees) {
    assignX(tree, xOffset);
    xOffset += tree.subtreeW + forestGap;
  }

  // Build lookup from parent+child slug pair to edge data
  const edgeDataMap = new Map<string, TreeEdgeData>();
  for (const edge of data.edges) {
    edgeDataMap.set(`${edge.parent}|${edge.child}`, edge);
  }

  // Flatten for rendering
  const allNodes: LayoutNode[] = [];
  const allEdges: FlatEdge[] = [];
  function flatten(node: LayoutNode, parent: LayoutNode | null): void {
    allNodes.push(node);
    if (parent) {
      const edgeData = edgeDataMap.get(`${parent.slug}|${node.slug}`) || null;
      allEdges.push({ from: parent, to: node, data: edgeData });
    }
    for (const child of node.children) {
      flatten(child, node);
    }
  }
  trees.forEach(t => flatten(t, null));

  // --- Compute bounds ---
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const n of allNodes) {
    minX = Math.min(minX, n.x - nodeW / 2);
    maxX = Math.max(maxX, n.x + nodeW / 2);
    minY = Math.min(minY, n.y);
    maxY = Math.max(maxY, n.y + nodeH);
  }
  const totalW = maxX - minX + padding * 2;
  const totalH = maxY - minY + padding * 2;
  const originX = minX - padding;
  const originY = minY - padding;

  // --- Create SVG ---
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('class', 'tree-svg');
  svg.setAttribute('viewBox', `${originX} ${originY} ${totalW} ${totalH}`);
  container.appendChild(svg);

  const edgeGroup = document.createElementNS(NS, 'g') as SVGGElement;
  edgeGroup.setAttribute('class', 'tree-edges');
  svg.appendChild(edgeGroup);

  const nodeGroup = document.createElementNS(NS, 'g') as SVGGElement;
  nodeGroup.setAttribute('class', 'tree-nodes');
  svg.appendChild(nodeGroup);

  // --- Render edges (clickable, with invisible wider hit area) ---
  let activeEdgePath: SVGPathElement | null = null; // currently highlighted edge path element

  function clearEdgeHighlight(): void {
    if (activeEdgePath) {
      activeEdgePath.setAttribute('stroke', COLORS.edge);
      activeEdgePath.setAttribute('stroke-width', '1.5');
      activeEdgePath = null;
    }
  }

  for (const { from, to, data: edgeData } of allEdges) {
    const x1 = from.x;
    const y1 = from.y + nodeH;
    const x2 = to.x;
    const y2 = to.y;
    const midY = (y1 + y2) / 2;
    const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

    const g = document.createElementNS(NS, 'g') as SVGGElement;
    g.setAttribute('class', 'tree-edge');
    g.setAttribute('data-from', from.slug);
    g.setAttribute('data-to', to.slug);
    g.style.cursor = 'pointer';

    // Invisible wider path for easier clicking
    const hitArea = document.createElementNS(NS, 'path') as SVGPathElement;
    hitArea.setAttribute('d', d);
    hitArea.setAttribute('fill', 'none');
    hitArea.setAttribute('stroke', 'transparent');
    hitArea.setAttribute('stroke-width', '14');
    g.appendChild(hitArea);

    // Visible path
    const path = document.createElementNS(NS, 'path') as SVGPathElement;
    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', COLORS.edge);
    path.setAttribute('stroke-width', '1.5');
    g.appendChild(path);

    g.addEventListener('mouseenter', () => {
      if (activeEdgePath !== path) {
        path.setAttribute('stroke', COLORS.edgeHover);
        path.setAttribute('stroke-width', '2.5');
      }
    });
    g.addEventListener('mouseleave', () => {
      if (activeEdgePath !== path) {
        path.setAttribute('stroke', COLORS.edge);
        path.setAttribute('stroke-width', '1.5');
      }
    });
    g.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      clearEdgeHighlight();
      // Highlight the clicked edge
      path.setAttribute('stroke', COLORS.edgeHover);
      path.setAttribute('stroke-width', '2.5');
      activeEdgePath = path;

      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      zoomToEdge(cx, cy);
      onEdgeClick({ edgeData, parentSlug: from.slug, childSlug: to.slug });
    });

    edgeGroup.appendChild(g);
  }

  // --- Render nodes ---
  for (const layoutNode of allNodes) {
    const concept = nodeMap.get(layoutNode.slug);
    if (!concept) continue;

    const borderColor = getNodeColorFromTags(concept.tags as any, 'hex') as string;

    const g = document.createElementNS(NS, 'g') as SVGGElement;
    g.setAttribute('class', 'tree-node');
    g.setAttribute('data-slug', layoutNode.slug);
    g.setAttribute('transform', `translate(${layoutNode.x - nodeW / 2}, ${layoutNode.y})`);
    g.style.cursor = 'pointer';

    const rect = document.createElementNS(NS, 'rect') as SVGRectElement;
    rect.setAttribute('width', String(nodeW));
    rect.setAttribute('height', String(nodeH));
    rect.setAttribute('rx', '8');
    rect.setAttribute('fill', COLORS.nodeFill);
    rect.setAttribute('stroke', borderColor);
    rect.setAttribute('stroke-width', '2');
    g.appendChild(rect);

    const title = concept.title || concept.slug;
    const displayTitle = title.length > 22 ? title.slice(0, 20) + '\u2026' : title;

    const text = document.createElementNS(NS, 'text') as SVGTextElement;
    text.setAttribute('x', String(nodeW / 2));
    text.setAttribute('y', String(nodeH / 2));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('fill', COLORS.text);
    text.setAttribute('font-family', "'JetBrains Mono', monospace");
    text.setAttribute('font-size', '13');
    text.textContent = displayTitle;
    g.appendChild(text);

    // Hover effects
    g.addEventListener('mouseenter', () => {
      rect.setAttribute('fill', COLORS.nodeHover);
      rect.setAttribute('stroke-width', '3');
    });
    g.addEventListener('mouseleave', () => {
      rect.setAttribute('fill', COLORS.nodeFill);
      rect.setAttribute('stroke-width', '2');
    });
    g.addEventListener('click', () => onNodeClick(concept.slug));

    nodeGroup.appendChild(g);
  }

  // --- Zoom & Pan ---
  // viewBox is tracked as (vbX, vbY) top-left corner, with size derived from scale
  let scale = 1;
  let vbX = originX;
  let vbY = originY;
  let dragging = false;
  let dragStartX: number, dragStartY: number, vbXStart: number, vbYStart: number;

  function updateViewBox(): void {
    const w = totalW / scale;
    const h = totalH / scale;
    svg.setAttribute('viewBox', `${vbX} ${vbY} ${w} ${h}`);
  }

  // Convert screen pixel position to SVG coordinates
  function screenToSVG(clientX: number, clientY: number): { x: number; y: number } {
    const rect = svg.getBoundingClientRect();
    const vbW = totalW / scale;
    const vbH = totalH / scale;
    const svgX = vbX + (clientX - rect.left) / rect.width * vbW;
    const svgY = vbY + (clientY - rect.top) / rect.height * vbH;
    return { x: svgX, y: svgY };
  }

  svg.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.2, Math.min(scale * factor, 15));

    // Zoom centered on mouse position
    const mouse = screenToSVG(e.clientX, e.clientY);
    const ratio = scale / newScale;
    vbX = mouse.x - (mouse.x - vbX) * ratio;
    vbY = mouse.y - (mouse.y - vbY) * ratio;
    scale = newScale;

    updateViewBox();
  }, { passive: false });

  svg.addEventListener('mousedown', (e: MouseEvent) => {
    if ((e.target as Element).closest('.tree-node') || (e.target as Element).closest('.tree-edge')) return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    vbXStart = vbX;
    vbYStart = vbY;
    svg.style.cursor = 'grabbing';
  });

  svg.addEventListener('mousemove', (e: MouseEvent) => {
    if (!dragging) return;
    // Convert pixel delta to SVG units so the content follows the mouse 1:1
    const rect = svg.getBoundingClientRect();
    const vbW = totalW / scale;
    const vbH = totalH / scale;
    const dx = (e.clientX - dragStartX) / rect.width * vbW;
    const dy = (e.clientY - dragStartY) / rect.height * vbH;
    vbX = vbXStart - dx;
    vbY = vbYStart - dy;
    updateViewBox();
  });

  function stopDrag(): void {
    dragging = false;
    svg.style.cursor = 'grab';
  }
  svg.addEventListener('mouseup', stopDrag);
  svg.addEventListener('mouseleave', stopDrag);

  svg.style.cursor = 'grab';

  // --- Zoom animation ---
  function animateViewBox(targetVbX: number, targetVbY: number, targetScale: number, duration: number = 400): void {
    const startVbX = vbX, startVbY = vbY, startScale = scale;
    const startTime = performance.now();

    function tick(now: number): void {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      scale = startScale + (targetScale - startScale) * ease;
      vbX = startVbX + (targetVbX - startVbX) * ease;
      vbY = startVbY + (targetVbY - startVbY) * ease;
      updateViewBox();

      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function zoomToEdge(cx: number, cy: number): void {
    const targetScale = Math.max(scale, 2.5);
    const vbW = totalW / targetScale;
    const vbH = totalH / targetScale;
    animateViewBox(cx - vbW / 2, cy - vbH / 2, targetScale);
  }

  return {
    clearEdgeHighlight,
    highlightNodes(slugs: string[] | null): void {
      const matchSet = slugs && slugs.length > 0 ? new Set(slugs) : null;

      for (const g of nodeGroup.querySelectorAll('.tree-node') as NodeListOf<SVGGElement>) {
        const slug = g.getAttribute('data-slug');
        (g as SVGGElement).style.opacity = !matchSet || matchSet.has(slug!) ? '1' : '0.15';
      }

      for (const g of edgeGroup.querySelectorAll('.tree-edge') as NodeListOf<SVGGElement>) {
        const from = g.getAttribute('data-from');
        const to = g.getAttribute('data-to');
        const connected = !matchSet || matchSet.has(from!) || matchSet.has(to!);
        (g as SVGGElement).style.opacity = connected ? '1' : '0.08';
      }
    },
    destroy(): void {
      container.innerHTML = '';
    },
  };
}
